import { useState } from "react";
import { joinRoom } from "../lib/api";
import { getFingerprint } from "../lib/fingerprint";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

interface Props {
  code: string;
  roomName?: string | null;
  onJoined: (memberId: string) => void;
}

export function JoinForm({ code, roomName, onJoined }: Props) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleJoin() {
    if (!name.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const fingerprint = await getFingerprint();
      const { memberId } = await joinRoom(code, { name: name.trim(), fingerprint });
      localStorage.setItem(`rateyou_member_${code}`, memberId);
      onJoined(memberId);
    } catch {
      setError("Failed to join. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {roomName ? `Join "${roomName}"` : "Join Room"}
          </h1>
          <p className="text-sm text-muted-foreground">
            Code: <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-xs">{code}</span>
          </p>
          <p className="text-sm text-muted-foreground">
            Pick a name — you'll appear on the board for others to rate.
          </p>
        </div>

        <div className="space-y-3">
          <Input
            placeholder="Your display name"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !!name.trim() && handleJoin()}
            disabled={loading}
          />
          <Button
            className="w-full"
            onClick={handleJoin}
            disabled={loading || !name.trim()}
          >
            {loading ? "Joining..." : "Join →"}
          </Button>
        </div>

        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </div>
    </div>
  );
}
